import React from 'react';
import { AlertTriangle, X, Save, Loader2 } from 'lucide-react';
import { Change, TableConfig } from './types';
import { formatValue } from './formatter';

type ConfirmationModalProps = {
  changes: Change[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  config: TableConfig;
};

export const ConfirmationModal = ({ 
  changes, 
  onConfirm, 
  onCancel, 
  isLoading,
  config 
}: ConfirmationModalProps) => {
  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-2xl w-full">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-white" />
          <h3 className="text-lg font-semibold text-white">
            Confirmar Alterações
          </h3>
        </div>
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-gray-600 mb-4">
          As seguintes alterações serão aplicadas:
        </p>

        <div className="max-h-[60vh] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Célula
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Valor Original
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Novo Valor
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {changes.map((change, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {change.cell}
                  </td>
                  <td className="px-4 py-2 text-sm text-red-600 line-through">
                    {change.original}
                  </td>
                  <td className="px-4 py-2 text-sm text-green-600 font-medium">
                    {change.new}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Confirmar</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};